import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js'
import {
  DEV_SESSION_KEY,
  isDevAdminCredentials,
  OPEN_DEV_PROFILE,
  SKIP_SUPABASE_AUTH,
  SUPABASE_CONFIGURED,
} from '../lib/auth-config'
import { perfilVeValoresMonetarios } from '../lib/perfilAcesso'
import { mensagemErroAuth } from '../lib/authErrors'
import { supabase } from '../lib/supabase'
import type { PerfilUsuario, Usuario } from '../types/database'

interface AuthState {
  session: Session | null
  user: User | null
  profile: Usuario | null
  /** True apenas na carga inicial da sessão (boot). Renovação de token não altera. */
  initializing: boolean
  unauthorized: boolean
  /** Detalhe quando perfil/sessão falha (ex.: JWT / relógio). */
  authFailureMessage: string | null
  signInWithPassword: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  canWrite: (resource: ResourceKey) => boolean
  canDelete: (resource: ResourceKey) => boolean
  /** Valores em R$ (contrato, preços, custos) — admin e Nutricionista ISEN (fiscal). */
  canViewValores: boolean
  isAdmin: boolean
  isLocalDev: boolean
}

type ResourceKey =
  | 'escolas'
  | 'rotas'
  | 'cardapio'
  | 'macronutrientes'
  | 'valores_referencia'
  | 'ficha_tecnica'
  | 'pratos'
  | 'precos'
  | 'entregas'
  | 'planejamento_rotas'
  | 'estoque'
  | 'ajustes_estoque'
  | 'calendario'
  | 'contrato'
  | 'compras'
  | 'fornecedores'
  | 'configuracoes'
  | 'usuarios'
  | 'auditoria'

/** `fiscal` = Nutricionista ISEN: ficha/cardápio/pedido; sem planejamento/entregas write. */
const WRITE_MATRIX: Record<ResourceKey, PerfilUsuario[]> = {
  escolas: ['admin'],
  rotas: ['admin', 'logistica'],
  cardapio: ['admin', 'nutricionista', 'fiscal'],
  macronutrientes: ['admin', 'nutricionista'],
  valores_referencia: ['admin', 'nutricionista'],
  ficha_tecnica: ['admin', 'nutricionista', 'fiscal'],
  pratos: ['admin', 'nutricionista', 'fiscal'],
  precos: ['admin'],
  entregas: ['admin', 'logistica', 'responsavel_escola', 'nutricionista'],
  planejamento_rotas: ['admin', 'nutricionista'],
  estoque: ['admin', 'nutricionista'],
  ajustes_estoque: ['admin', 'nutricionista'],
  calendario: ['admin'],
  contrato: ['admin', 'fiscal'],
  compras: ['admin', 'fiscal'],
  fornecedores: ['admin'],
  configuracoes: ['admin'],
  usuarios: ['admin'],
  auditoria: [],
}

const DELETE_MATRIX: Record<ResourceKey, PerfilUsuario[]> = {
  escolas: ['admin'],
  rotas: ['admin'],
  cardapio: ['admin', 'nutricionista', 'fiscal'],
  macronutrientes: ['admin', 'nutricionista'],
  valores_referencia: ['admin', 'nutricionista'],
  ficha_tecnica: ['admin', 'nutricionista', 'fiscal'],
  pratos: ['admin', 'nutricionista', 'fiscal'],
  precos: ['admin'],
  entregas: ['admin'],
  planejamento_rotas: ['admin'],
  estoque: ['admin'],
  ajustes_estoque: ['admin'],
  calendario: ['admin'],
  contrato: ['admin'],
  compras: ['admin', 'fiscal'],
  fornecedores: ['admin'],
  configuracoes: [],
  usuarios: ['admin'],
  auditoria: [],
}

const AuthContext = createContext<AuthState | null>(null)

function mockDevSession(): Session {
  return {
    user: { id: OPEN_DEV_PROFILE.id, email: OPEN_DEV_PROFILE.email } as User,
  } as Session
}

function restoreDevSession(): boolean {
  try {
    return localStorage.getItem(DEV_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

function persistDevSession(active: boolean) {
  try {
    if (active) localStorage.setItem(DEV_SESSION_KEY, '1')
    else localStorage.removeItem(DEV_SESSION_KEY)
  } catch {
    /* ignore */
  }
}

function applyDevLogin(
  setSession: (s: Session | null) => void,
  setProfile: (p: Usuario | null) => void,
  setUnauthorized: (v: boolean) => void,
  setInitializing: (v: boolean) => void,
) {
  setSession(mockDevSession())
  setProfile(OPEN_DEV_PROFILE)
  setUnauthorized(false)
  setInitializing(false)
  persistDevSession(true)
}

function shouldReloadProfile(event: AuthChangeEvent) {
  return event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'MFA_CHALLENGE_VERIFIED'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const startAsDev =
    SKIP_SUPABASE_AUTH && (restoreDevSession() || SKIP_SUPABASE_AUTH)

  const [session, setSession] = useState<Session | null>(
    startAsDev ? mockDevSession() : null,
  )
  const [profile, setProfile] = useState<Usuario | null>(
    startAsDev ? OPEN_DEV_PROFILE : null,
  )
  const [initializing, setInitializing] = useState(!startAsDev)
  const [unauthorized, setUnauthorized] = useState(false)
  const [authFailureMessage, setAuthFailureMessage] = useState<string | null>(null)
  const bootstrappedRef = useRef(startAsDev)

  const loadProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('usuarios')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (error) {
      const friendly = mensagemErroAuth(error.message)
      console.error('Erro ao carregar perfil:', error.message)
      setProfile(null)
      setAuthFailureMessage(friendly)
      setUnauthorized(true)
      return
    }

    if (!data || !data.ativo) {
      setProfile(null)
      setAuthFailureMessage(
        data
          ? 'Usuário inativo no cadastro. Contate o administrador.'
          : 'Cadastro não encontrado para esta conta.',
      )
      setUnauthorized(true)
      return
    }

    setProfile(data as Usuario)
    setUnauthorized(false)
    setAuthFailureMessage(null)
  }, [])

  const refreshProfile = useCallback(async () => {
    if (session?.user?.id) {
      await loadProfile(session.user.id)
    }
  }, [loadProfile, session?.user?.id])

  useEffect(() => {
    if (SKIP_SUPABASE_AUTH) {
      applyDevLogin(setSession, setProfile, setUnauthorized, setInitializing)
      return
    }

    let mounted = true

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return
        setSession(data.session)
        if (data.session?.user) {
          loadProfile(data.session.user.id).finally(() => {
            if (mounted) {
              bootstrappedRef.current = true
              setInitializing(false)
            }
          })
        } else {
          bootstrappedRef.current = true
          setInitializing(false)
        }
      })
      .catch((err: unknown) => {
        // Sem isso, uma falha em getSession deixaria `initializing` travado para sempre.
        console.error('Erro ao restaurar sessão:', err instanceof Error ? err.message : err)
        if (!mounted) return
        setSession(null)
        bootstrappedRef.current = true
        setInitializing(false)
      })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return

      setSession(nextSession)

      // Renovação silenciosa: não desmonta a app.
      if (event === 'TOKEN_REFRESHED') return

      if (event === 'SIGNED_OUT' || !nextSession?.user) {
        setProfile(null)
        setUnauthorized(false)
        return
      }

      if (event === 'INITIAL_SESSION') {
        if (bootstrappedRef.current) return
        loadProfile(nextSession.user.id).finally(() => {
          if (mounted) {
            bootstrappedRef.current = true
            setInitializing(false)
          }
        })
        return
      }

      if (shouldReloadProfile(event)) {
        void loadProfile(nextSession.user.id)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [loadProfile])

  useEffect(() => {
    if (!SKIP_SUPABASE_AUTH || profile) return
    applyDevLogin(setSession, setProfile, setUnauthorized, setInitializing)
  }, [profile])

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    if (!SUPABASE_CONFIGURED && isDevAdminCredentials(email, password)) {
      applyDevLogin(setSession, setProfile, setUnauthorized, setInitializing)
      return
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw new Error(mensagemErroAuth(error.message))
  }, [])

  const signOut = useCallback(async () => {
    if (SKIP_SUPABASE_AUTH) {
      applyDevLogin(setSession, setProfile, setUnauthorized, setInitializing)
      return
    }
    if (!SUPABASE_CONFIGURED && profile?.id === OPEN_DEV_PROFILE.id) {
      persistDevSession(false)
      setSession(null)
      setProfile(null)
      setUnauthorized(false)
      setAuthFailureMessage(null)
      return
    }
    await supabase.auth.signOut()
    setProfile(null)
    setUnauthorized(false)
    setAuthFailureMessage(null)
  }, [profile?.id])

  const canWrite = useCallback(
    (resource: ResourceKey) => {
      if (SKIP_SUPABASE_AUTH) return true
      if (!profile) return false
      return WRITE_MATRIX[resource].includes(profile.perfil)
    },
    [profile],
  )

  const canDelete = useCallback(
    (resource: ResourceKey) => {
      if (SKIP_SUPABASE_AUTH) return true
      if (!profile) return false
      return DELETE_MATRIX[resource].includes(profile.perfil)
    },
    [profile],
  )

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      initializing,
      unauthorized,
      authFailureMessage,
      signInWithPassword,
      signOut,
      refreshProfile,
      canWrite,
      canDelete,
      canViewValores: SKIP_SUPABASE_AUTH || perfilVeValoresMonetarios(profile?.perfil),
      isAdmin: SKIP_SUPABASE_AUTH || profile?.perfil === 'admin',
      isLocalDev: SKIP_SUPABASE_AUTH,
    }),
    [
      session,
      profile,
      initializing,
      unauthorized,
      authFailureMessage,
      signInWithPassword,
      signOut,
      refreshProfile,
      canWrite,
      canDelete,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider')
  return ctx
}
